import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { debounceTime, finalize, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';

import { CompaniesService } from '../../../../services/companies';
import { CountryCityService } from '../../../../services/country-city.service';
import { ResigteringService } from '../../../../services/resigtering.service';
import { LoginService } from '../../../../services/login.service';
import { TranslocoService } from '@ngneat/transloco';

type CompanyStep = 'company' | 'contact' | 'logo';

interface LoginResponse {
  token: string;
  user?: {
    id?: number;
    username?: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    phone?: string;
    type?: number | string;
    user_type?: 'candidate' | 'admin' | 'company';
    company_id?: number | null;
    doctor_id?: number | null;
    access_pages?: string[];
    has_access_to_phone_numbers?: boolean;
  };
}

@Component({
  selector: 'app-createcompany',
  templateUrl: './createcompany.component.html',
  styleUrl: './createcompany.component.scss',
})
export class CreatecompanyComponent implements OnInit, OnDestroy {
  companyForm: FormGroup;

  countries: any[] = [];
  cities: any[] = [];

  selectedCountryId = '';
  selectedCityId = '';

  currentStep: CompanyStep = 'company';
  readonly stepOrder: CompanyStep[] = ['company', 'contact', 'logo'];

  selectedLogo: File | null = null;
  logoPreview: string | ArrayBuffer | null = null;
  logoDragOver = false;

  isLoading = false;
  isCountriesLoading = false;
  isCitiesLoading = false;

  successMessage = '';
  errorMessage = '';

  private readonly maxLogoSize = 4 * 1024 * 1024;

  private readonly draftKey = 'atw_create_company_form_draft_v2';
  private readonly countryStorageKey = 'atw_create_company_country_id';
  private readonly cityStorageKey = 'atw_create_company_city_id';

  private readonly destroy$ = new Subject<void>();

  private readonly stepFields: Record<CompanyStep, string[]> = {
    company: [
      'name',
      'name_ar',
      'specialty',
      'industry',
      'country_id',
      'city_id',
      'address',
    ],
    contact: ['tel', 'fax', 'contact_email', 'facebook', 'linkedin'],
    logo: [],
  };

  constructor(
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    private countryCityService: CountryCityService,
    private resigteringService: ResigteringService,
    private loginService: LoginService,
    private router: Router,
    private transloco: TranslocoService
  ) {
    this.companyForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      name_ar: ['', [Validators.maxLength(255)]],

      specialty: ['', [Validators.maxLength(255)]],
      industry: ['', [Validators.maxLength(255)]],

      country_id: ['', [Validators.required]],
      city_id: ['', [Validators.required]],

      tel: ['', [Validators.required, Validators.maxLength(30)]],
      fax: ['', [Validators.maxLength(50)]],

      address: ['', [Validators.maxLength(1000)]],
      contact_email: ['', [Validators.email, Validators.maxLength(255)]],

      facebook: ['', [Validators.maxLength(255)]],
      linkedin: ['', [Validators.maxLength(255)]],

      is_user: [true],
      is_ahmed_elshahat_client: [false],
    });
  }

  ngOnInit(): void {
    const restored = this.restoreDraft();

    this.loadCountries();

    if (restored.countryId) {
      this.loadCitiesByCountry(restored.countryId, restored.cityId);
    }

    this.watchDraftChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get f() {
    return this.companyForm.controls;
  }

  get isAr(): boolean {
    return this.transloco.getActiveLang() === 'ar';
  }

  handleSubmit(): void {
    if (this.currentStep !== 'logo') {
      this.goNext();
      return;
    }

    this.onSubmit();
  }

  goNext(): void {
    this.successMessage = '';

    if (!this.validateStep(this.currentStep)) {
      return;
    }

    const currentIndex = this.stepOrder.indexOf(this.currentStep);
    const nextStep = this.stepOrder[currentIndex + 1];

    if (nextStep) {
      this.errorMessage = '';
      this.currentStep = nextStep;
      this.saveDraft();
    }
  }

  goBack(): void {
    this.successMessage = '';
    this.errorMessage = '';

    const currentIndex = this.stepOrder.indexOf(this.currentStep);
    const previousStep = this.stepOrder[currentIndex - 1];

    if (previousStep) {
      this.currentStep = previousStep;

      if (previousStep === 'company') {
        this.restoreCityAfterBack();
      }

      this.saveDraft();
    }
  }

  goToStep(step: CompanyStep): void {
    this.successMessage = '';

    const currentIndex = this.stepOrder.indexOf(this.currentStep);
    const targetIndex = this.stepOrder.indexOf(step);

    if (targetIndex <= currentIndex) {
      this.errorMessage = '';
      this.currentStep = step;

      if (step === 'company') {
        this.restoreCityAfterBack();
      }

      this.saveDraft();
      return;
    }

    for (let index = 0; index < targetIndex; index++) {
      const stepToValidate = this.stepOrder[index];

      if (!this.validateStep(stepToValidate)) {
        this.currentStep = stepToValidate;
        this.saveDraft();
        return;
      }
    }

    this.errorMessage = '';
    this.currentStep = step;
    this.saveDraft();
  }

  isStepDone(step: CompanyStep): boolean {
    const stepIndex = this.stepOrder.indexOf(step);
    const currentIndex = this.stepOrder.indexOf(this.currentStep);

    return stepIndex < currentIndex && this.isStepFormValid(step);
  }

  loadCountries(): void {
    this.isCountriesLoading = true;

    this.countryCityService
      .getCountries({ per_page: 1000 })
      .pipe(finalize(() => (this.isCountriesLoading = false)))
      .subscribe({
        next: (res) => {
          this.countries = this.extractList(res).map((country) =>
            this.normalizeCountryItem(country)
          );
        },
        error: () => {
          this.errorMessage = this.t('atw.createCompany.messages.countriesError');
        },
      });
  }

  loadCitiesByCountry(countryId: any, cityIdToSelect?: any): void {
    const normalizedCountryId = this.normalizeId(countryId);

    if (!normalizedCountryId) {
      this.cities = [];
      this.selectedCountryId = '';
      this.selectedCityId = '';
      this.patchCompanyIds('', '');
      this.removeStorageItem(this.countryStorageKey);
      this.removeStorageItem(this.cityStorageKey);
      this.saveDraft();
      return;
    }

    this.selectedCountryId = normalizedCountryId;
    this.setStorageItem(this.countryStorageKey, normalizedCountryId);

    this.isCitiesLoading = true;

    this.countryCityService
      .getCitiesByCountry(normalizedCountryId)
      .pipe(finalize(() => (this.isCitiesLoading = false)))
      .subscribe({
        next: (res) => {
          this.cities = this.extractList(res).map((city) =>
            this.normalizeCityItem(city)
          );

          const cityId =
            this.normalizeId(cityIdToSelect) ||
            this.normalizeId(this.selectedCityId) ||
            this.normalizeId(this.getStorageItem(this.cityStorageKey)) ||
            this.normalizeId(this.companyForm.get('city_id')?.value);

          if (cityId) {
            this.selectedCityId = cityId;
            this.setStorageItem(this.cityStorageKey, cityId);

            this.companyForm.patchValue(
              {
                country_id: normalizedCountryId,
                city_id: cityId,
              },
              { emitEvent: false }
            );

            setTimeout(() => {
              this.companyForm.patchValue(
                {
                  country_id: normalizedCountryId,
                  city_id: cityId,
                },
                { emitEvent: false }
              );

              this.saveDraft();
            });
          } else {
            this.companyForm.patchValue(
              {
                country_id: normalizedCountryId,
              },
              { emitEvent: false }
            );

            this.saveDraft();
          }

          console.log('COUNTRY ID:', this.selectedCountryId);
          console.log('CITIES LIST:', this.cities);
          console.log('CITY ID AFTER LOAD:', this.selectedCityId);
          console.log('FORM VALUE AFTER LOAD:', this.companyForm.getRawValue());
        },
        error: () => {
          this.errorMessage = this.t('atw.createCompany.messages.citiesError');
        },
      });
  }

  onCountryChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const countryId = this.normalizeId(select.value);

    this.selectedCountryId = countryId;
    this.selectedCityId = '';

    this.patchCompanyIds(countryId, '');

    this.setStorageItem(this.countryStorageKey, countryId);
    this.removeStorageItem(this.cityStorageKey);

    this.cities = [];
    this.saveDraft();

    if (countryId) {
      this.loadCitiesByCountry(countryId);
    }

    console.log('COUNTRY SELECTED:', countryId);
    console.log('FORM AFTER COUNTRY:', this.companyForm.getRawValue());
  }

  onCityChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const cityId = this.normalizeId(select.value);

    this.selectedCityId = cityId;

    this.companyForm.patchValue(
      {
        city_id: cityId,
      },
      { emitEvent: false }
    );

    if (cityId) {
      this.setStorageItem(this.cityStorageKey, cityId);
    } else {
      this.removeStorageItem(this.cityStorageKey);
    }

    this.saveDraft();

    console.log('CITY SELECTED FROM EVENT:', cityId);
    console.log('CITY FORM CONTROL:', this.companyForm.get('city_id')?.value);
    console.log('FULL FORM:', this.companyForm.getRawValue());
  }

  getCountryId(country: any): string {
    return this.normalizeId(
      country?.__id ??
        country?.id ??
        country?.country_id ??
        country?.countries_id ??
        country?.countryId ??
        country?.CountryID ??
        country?.value ??
        country?.country?.id ??
        country?.country?.country_id ??
        ''
    );
  }

  getCityId(city: any): string {
    return this.normalizeId(
      city?.__id ??
        city?.id ??
        city?.city_id ??
        city?.cities_id ??
        city?.cityId ??
        city?.CityID ??
        city?.value ??
        city?.city?.id ??
        city?.city?.city_id ??
        city?.city?.cities_id ??
        city?.pivot?.city_id ??
        city?.pivot?.cities_id ??
        ''
    );
  }

  getCountryName(country: any): string {
    const lang = this.transloco.getActiveLang();

    if (lang === 'ar') {
      return (
        country?.__nameAr ||
        country?.country_name_ar ||
        country?.name_ar ||
        country?.country?.country_name_ar ||
        country?.country?.name_ar ||
        country?.__nameEn ||
        country?.country_name_en ||
        country?.name ||
        ''
      );
    }

    return (
      country?.__nameEn ||
      country?.country_name_en ||
      country?.name ||
      country?.country?.country_name_en ||
      country?.country?.name ||
      country?.__nameAr ||
      country?.country_name_ar ||
      country?.name_ar ||
      ''
    );
  }

  getCityName(city: any): string {
    const lang = this.transloco.getActiveLang();

    if (lang === 'ar') {
      return (
        city?.__nameAr ||
        city?.city_name_ar ||
        city?.city?.city_name_ar ||
        city?.name_ar ||
        city?.city?.name_ar ||
        city?.__nameEn ||
        city?.city_name_en ||
        city?.city?.city_name_en ||
        city?.name ||
        city?.city?.name ||
        ''
      );
    }

    return (
      city?.__nameEn ||
      city?.city_name_en ||
      city?.city?.city_name_en ||
      city?.name ||
      city?.city?.name ||
      city?.__nameAr ||
      city?.city_name_ar ||
      city?.city?.city_name_ar ||
      city?.name_ar ||
      city?.city?.name_ar ||
      ''
    );
  }

trackByCountryId = (index: number, country: any): string => {
  return (
    this.normalizeId(
      country?.__id ??
        country?.id ??
        country?.country_id ??
        country?.countries_id ??
        country?.countryId ??
        country?.CountryID ??
        country?.value ??
        country?.country?.id ??
        country?.country?.country_id ??
        ''
    ) || String(index)
  );
};

trackByCityId = (index: number, city: any): string => {
  return (
    this.normalizeId(
      city?.__id ??
        city?.id ??
        city?.city_id ??
        city?.cities_id ??
        city?.cityId ??
        city?.CityID ??
        city?.value ??
        city?.city?.id ??
        city?.city?.city_id ??
        city?.city?.cities_id ??
        city?.pivot?.city_id ??
        city?.pivot?.cities_id ??
        ''
    ) || String(index)
  );
};

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file) {
      this.setLogo(file);
    }

    input.value = '';
  }

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    this.logoDragOver = true;
  }

  onLogoDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.logoDragOver = false;
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    this.logoDragOver = false;

    const file = event.dataTransfer?.files?.[0];

    if (file) {
      this.setLogo(file);
    }
  }

  setLogo(file: File): void {
    this.errorMessage = '';
    this.successMessage = '';

    const fileName = file.name.toLowerCase();

    const isAllowed =
      file.type === 'image/jpeg' ||
      file.type === 'image/jpg' ||
      file.type === 'image/png' ||
      file.type === 'image/webp' ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.webp');

    if (!isAllowed) {
      this.selectedLogo = null;
      this.logoPreview = null;
      this.errorMessage = this.t('atw.createCompany.messages.logoTypeError');
      return;
    }

    if (file.size > this.maxLogoSize) {
      this.selectedLogo = null;
      this.logoPreview = null;
      this.errorMessage = this.t('atw.createCompany.messages.logoSizeError');
      return;
    }

    this.selectedLogo = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.logoPreview = reader.result;
    };

    reader.readAsDataURL(file);
  }

  removeLogo(event?: MouseEvent, input?: HTMLInputElement): void {
    if (event) {
      event.stopPropagation();
    }

    this.selectedLogo = null;
    this.logoPreview = null;

    if (input) {
      input.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (!bytes) {
      return '0 KB';
    }

    const kb = bytes / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    return `${(kb / 1024).toFixed(1)} MB`;
  }

  getLogoTypeLabel(file: File | null): string {
    if (!file) {
      return 'LOGO';
    }

    const name = file.name.toLowerCase();

    if (name.endsWith('.png')) {
      return 'PNG';
    }

    if (name.endsWith('.webp')) {
      return 'WEBP';
    }

    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
      return 'JPG';
    }

    return 'LOGO';
  }

  onSubmit(): void {
    this.successMessage = '';
    this.errorMessage = '';

    this.restoreCityFromStorageBeforeSubmit();

    const firstInvalidStep = this.getFirstInvalidStep();

    if (firstInvalidStep) {
      this.currentStep = firstInvalidStep;
      this.validateStep(firstInvalidStep);
      this.saveDraft();
      return;
    }

    const value = this.companyForm.getRawValue();
    const mobile = String(value.tel || '').trim();

    if (!mobile) {
      this.errorMessage = this.t('atw.createCompany.validation.telRequired');
      this.companyForm.get('tel')?.markAsTouched();
      this.currentStep = 'contact';
      this.saveDraft();
      return;
    }

    const userPayload = this.buildRegisterPayload(value, mobile);
    let createdUserId: number | string | null = null;
    let createdCompanyId: number | string | null = null;

    this.isLoading = true;

    this.resigteringService
      .user_Resigter(userPayload)
      .pipe(
        switchMap((registerResponse: any) => {
          const formData = this.buildCompanyFormData();

          createdUserId =
            registerResponse?.data?.id ||
            registerResponse?.user?.id ||
            registerResponse?.id;

          if (createdUserId) {
            formData.append('user_id', String(createdUserId));
          }

          return this.companiesService.create(formData);
        }),

        switchMap((companyResponse: any) => {
          createdCompanyId = this.extractCompanyId(companyResponse);

          return this.loginService.user_login({
            username: mobile,
            password: mobile,
          });
        }),

        switchMap((loginResponse: LoginResponse) => {
          if (!loginResponse?.token) {
            return of(loginResponse);
          }

          this.saveLoginSession(loginResponse, createdCompanyId);

          if (createdUserId && createdCompanyId) {
            return this.resigteringService
              .editUser(
                createdUserId,
                this.buildCompanyUserUpdateFormData(createdCompanyId, value, mobile)
              )
              .pipe(
                map((updateResponse: any) => ({
                  ...loginResponse,
                  user: {
                    ...(loginResponse.user || {}),
                    ...(updateResponse?.user || {}),
                    company_id: createdCompanyId,
                    user_type: 'company' as const,
                  },
                }))
              );
          }

          return of(loginResponse);
        }),

        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (loginResponse: LoginResponse) => {
          if (!loginResponse?.token) {
            this.errorMessage = this.isAr
              ? 'تم إنشاء الحساب لكن لم يتم تسجيل الدخول تلقائيًا'
              : 'Account created but auto login failed';

            Swal.fire({
              icon: 'error',
              title: this.isAr ? 'مشكلة في تسجيل الدخول' : 'Login problem',
              text: this.errorMessage,
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });

            return;
          }

          this.saveLoginSession(loginResponse, createdCompanyId);
          this.clearDraft();

          this.successMessage = this.t('atw.createCompany.messages.success');
          this.errorMessage = '';

          Swal.fire({
            icon: 'success',
            title: this.isAr
              ? 'أهلاً بيك في أحمد الشحات'
              : 'Welcome to Ahmed El-Shahat',
            showConfirmButton: false,
            timer: 1500,
          });

          this.router.navigate(['/create-job']);
        },

        error: (error) => {
          this.errorMessage = this.getApiErrorMessage(error);

          Swal.fire({
            icon: 'error',
            title: this.isAr ? 'حصل خطأ' : 'Something went wrong',
            text: this.errorMessage,
            confirmButtonText: this.isAr ? 'تمام' : 'OK',
          });
        },
      });
  }

  private validateStep(step: CompanyStep): boolean {
    const fields = this.stepFields[step];
    let isValid = true;

    fields.forEach((fieldName) => {
      const control = this.companyForm.get(fieldName);

      if (!control) {
        return;
      }

      control.markAsTouched();
      control.updateValueAndValidity();

      if (control.invalid) {
        isValid = false;
      }
    });

    if (!isValid) {
      this.errorMessage = this.t('atw.createCompany.messages.invalidForm');
    }

    return isValid;
  }

  private isStepFormValid(step: CompanyStep): boolean {
    const fields = this.stepFields[step];

    return fields.every((fieldName) => {
      const control = this.companyForm.get(fieldName);
      return !control || control.valid;
    });
  }

  private getFirstInvalidStep(): CompanyStep | null {
    for (const step of this.stepOrder) {
      if (!this.isStepFormValid(step)) {
        return step;
      }
    }

    return null;
  }

  private buildRegisterPayload(value: any, mobile: string): FormData {
    const cleanMobile = mobile.replace(/\D/g, '') || mobile;
    const formData = new FormData();

    formData.append('username', mobile);
    formData.append('type', '9');
    formData.append('user_type', 'company');
    formData.append('role', 'company');

    formData.append('first_name', String(value.name || '').trim());
    formData.append('last_name', String(value.name_ar || value.name || '').trim());
    formData.append('email', value.contact_email || `${cleanMobile}@company.local`);

    formData.append('phone', mobile);
    formData.append('mobile', mobile);
    formData.append('tel', mobile);

    formData.append('password', mobile);
    formData.append('password_confirmation', mobile);

    formData.append('company_id', '');
    formData.append('doctor_id', '');
    formData.append('is_active', '1');

    return formData;
  }

  private buildCompanyFormData(): FormData {
    this.restoreCityFromStorageBeforeSubmit();

    const formData = new FormData();
    const value = this.companyForm.getRawValue();

    const countryId =
      this.normalizeId(value.country_id) ||
      this.normalizeId(this.selectedCountryId) ||
      this.normalizeId(this.getStorageItem(this.countryStorageKey));

    const cityId =
      this.normalizeId(value.city_id) ||
      this.normalizeId(this.selectedCityId) ||
      this.normalizeId(this.getStorageItem(this.cityStorageKey));

    const normalFields = [
      'name',
      'name_ar',
      'specialty',
      'industry',
      'tel',
      'fax',
      'address',
      'contact_email',
      'facebook',
      'linkedin',
    ];

    normalFields.forEach((key) => {
      const fieldValue = value[key];

      if (
        fieldValue !== null &&
        fieldValue !== undefined &&
        String(fieldValue).trim() !== ''
      ) {
        formData.append(key, String(fieldValue).trim());
      }
    });

    if (countryId) {
      formData.set('country_id', countryId);
    }

    if (cityId) {
      formData.set('city_id', cityId);

      // احتياطي لو الباك إند عنده العمود باسم cities_id
      formData.set('cities_id', cityId);
    }

    formData.append('is_user', value.is_user ? '1' : '0');
    formData.append(
      'is_ahmed_elshahat_client',
      value.is_ahmed_elshahat_client ? '1' : '0'
    );

    formData.append('add_by', 'user');

    if (this.selectedLogo) {
      formData.append('image', this.selectedLogo, this.selectedLogo.name);
    }

    console.log('FINAL COUNTRY BEFORE SEND:', countryId);
    console.log('FINAL CITY BEFORE SEND:', cityId);

    formData.forEach((v, k) => {
      console.log('FORMDATA SEND:', k, v);
    });

    return formData;
  }

  private buildCompanyUserUpdateFormData(
    companyId: number | string,
    value: any,
    mobile: string
  ): FormData {
    const formData = new FormData();
    const cleanMobile = mobile.replace(/\D/g, '') || mobile;

    formData.append('username', mobile);
    formData.append('first_name', String(value.name || '').trim());
    formData.append('last_name', String(value.name_ar || value.name || '').trim());
    formData.append('phone', mobile);
    formData.append('email', value.contact_email || `${cleanMobile}@company.local`);
    formData.append('company_id', String(companyId));
    formData.append('doctor_id', '');
    formData.append('user_type', 'company');
    formData.append('role', 'company');
    formData.append('type', '9');

    return formData;
  }

  private extractCompanyId(response: any): number | string | null {
    return (
      response?.data?.id ||
      response?.data?.company?.id ||
      response?.data?.data?.id ||
      response?.company?.id ||
      response?.id ||
      null
    );
  }

  private patchCompanyIds(countryId: string, cityId: string): void {
    this.companyForm.patchValue(
      {
        country_id: countryId,
        city_id: cityId,
      },
      { emitEvent: false }
    );
  }

  private restoreCityAfterBack(): void {
    const countryId =
      this.normalizeId(this.selectedCountryId) ||
      this.normalizeId(this.companyForm.get('country_id')?.value) ||
      this.normalizeId(this.getStorageItem(this.countryStorageKey));

    const cityId =
      this.normalizeId(this.selectedCityId) ||
      this.normalizeId(this.companyForm.get('city_id')?.value) ||
      this.normalizeId(this.getStorageItem(this.cityStorageKey));

    if (countryId) {
      this.selectedCountryId = countryId;
      this.companyForm.patchValue({ country_id: countryId }, { emitEvent: false });

      if (!this.cities.length) {
        this.loadCitiesByCountry(countryId, cityId);
      } else if (cityId) {
        this.selectedCityId = cityId;
        this.companyForm.patchValue({ city_id: cityId }, { emitEvent: false });
      }
    }
  }

  private restoreCityFromStorageBeforeSubmit(): void {
    const countryId =
      this.normalizeId(this.companyForm.get('country_id')?.value) ||
      this.normalizeId(this.selectedCountryId) ||
      this.normalizeId(this.getStorageItem(this.countryStorageKey));

    const cityId =
      this.normalizeId(this.companyForm.get('city_id')?.value) ||
      this.normalizeId(this.selectedCityId) ||
      this.normalizeId(this.getStorageItem(this.cityStorageKey));

    this.selectedCountryId = countryId;
    this.selectedCityId = cityId;

    this.companyForm.patchValue(
      {
        country_id: countryId,
        city_id: cityId,
      },
      { emitEvent: false }
    );

    if (countryId) {
      this.setStorageItem(this.countryStorageKey, countryId);
    }

    if (cityId) {
      this.setStorageItem(this.cityStorageKey, cityId);
    }
  }

  private watchDraftChanges(): void {
    this.companyForm.valueChanges
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe(() => {
        this.saveDraft();
      });
  }

  private saveDraft(): void {
    if (!this.canUseLocalStorage()) {
      return;
    }

    const value = this.companyForm.getRawValue();

    const countryId =
      this.normalizeId(value.country_id) ||
      this.normalizeId(this.selectedCountryId) ||
      this.normalizeId(this.getStorageItem(this.countryStorageKey));

    const cityId =
      this.normalizeId(value.city_id) ||
      this.normalizeId(this.selectedCityId) ||
      this.normalizeId(this.getStorageItem(this.cityStorageKey));

    const draft = {
      currentStep: this.currentStep,
      form: {
        ...value,
        country_id: countryId,
        city_id: cityId,
      },
    };

    if (countryId) {
      this.setStorageItem(this.countryStorageKey, countryId);
    }

    if (cityId) {
      this.setStorageItem(this.cityStorageKey, cityId);
    }

    localStorage.setItem(this.draftKey, JSON.stringify(draft));
  }

  private restoreDraft(): { countryId: string; cityId: string } {
    if (!this.canUseLocalStorage()) {
      return { countryId: '', cityId: '' };
    }

    const raw = localStorage.getItem(this.draftKey);

    let formValue: any = {};
    let draftStep: CompanyStep | null = null;

    if (raw) {
      try {
        const draft = JSON.parse(raw);
        formValue = draft?.form || {};

        if (this.isCompanyStep(draft?.currentStep)) {
          draftStep = draft.currentStep;
        }
      } catch {
        this.clearDraft();
      }
    }

    const countryId =
      this.normalizeId(formValue.country_id) ||
      this.normalizeId(this.getStorageItem(this.countryStorageKey));

    const cityId =
      this.normalizeId(formValue.city_id) ||
      this.normalizeId(this.getStorageItem(this.cityStorageKey));

    this.selectedCountryId = countryId;
    this.selectedCityId = cityId;

    this.companyForm.patchValue(
      {
        ...formValue,
        country_id: countryId,
        city_id: cityId,
      },
      { emitEvent: false }
    );

    if (draftStep) {
      this.currentStep = draftStep;
    }

    return { countryId, cityId };
  }

  private clearDraft(): void {
    if (!this.canUseLocalStorage()) {
      return;
    }

    localStorage.removeItem(this.draftKey);
    localStorage.removeItem(this.countryStorageKey);
    localStorage.removeItem(this.cityStorageKey);
  }

  private normalizeCountryItem(country: any): any {
    const id = this.normalizeId(
      country?.id ??
        country?.country_id ??
        country?.countries_id ??
        country?.countryId ??
        country?.CountryID ??
        country?.value ??
        country?.country?.id ??
        country?.country?.country_id ??
        ''
    );

    return {
      ...country,
      __id: id,
      __nameAr:
        country?.country_name_ar ||
        country?.name_ar ||
        country?.country?.country_name_ar ||
        country?.country?.name_ar ||
        '',
      __nameEn:
        country?.country_name_en ||
        country?.name ||
        country?.country?.country_name_en ||
        country?.country?.name ||
        '',
    };
  }

  private normalizeCityItem(city: any): any {
    const id = this.normalizeId(
      city?.id ??
        city?.city_id ??
        city?.cities_id ??
        city?.cityId ??
        city?.CityID ??
        city?.value ??
        city?.city?.id ??
        city?.city?.city_id ??
        city?.city?.cities_id ??
        city?.pivot?.city_id ??
        city?.pivot?.cities_id ??
        ''
    );

    return {
      ...city,
      __id: id,
      __nameAr:
        city?.city_name_ar ||
        city?.city?.city_name_ar ||
        city?.name_ar ||
        city?.city?.name_ar ||
        '',
      __nameEn:
        city?.city_name_en ||
        city?.city?.city_name_en ||
        city?.name ||
        city?.city?.name ||
        '',
    };
  }

  private extractList(res: any): any[] {
    const possibleLists = [
      res?.data?.data,
      res?.data?.cities,
      res?.data?.countries,
      res?.data?.items,
      res?.data,
      res?.cities,
      res?.countries,
      res?.items,
      res,
    ];

    const list = possibleLists.find((item) => Array.isArray(item));

    return Array.isArray(list) ? list : [];
  }

  private saveLoginSession(
    res: LoginResponse,
    fallbackCompanyId?: number | string | null
  ): void {
    if (!res?.token) {
      return;
    }

    if (this.canUseLocalStorage()) {
      if (res.user?.username) {
        localStorage.setItem('username', res.user.username);
      }

      if (res.user?.id !== undefined && res.user?.id !== null) {
        localStorage.setItem('user_id', String(res.user.id));
      }

      if (res.user?.first_name) {
        localStorage.setItem('first_name', res.user.first_name);
      }

      if (res.user?.last_name) {
        localStorage.setItem('last_name', res.user.last_name);
      }

      if (res.user?.user_type) {
        localStorage.setItem('user_type', res.user.user_type);
      }

      if (res.user?.type !== undefined && res.user?.type !== null) {
        localStorage.setItem('type', String(res.user.type));
      }

      const companyId = res.user?.company_id || fallbackCompanyId;

      if (companyId !== undefined && companyId !== null) {
        localStorage.setItem('company_id', String(companyId));
      } else {
        localStorage.removeItem('company_id');
      }

      localStorage.setItem(
        'user',
        JSON.stringify({
          ...(res.user || {}),
          user_type: res.user?.user_type || 'company',
          type: res.user?.type ?? 9,
          company_id: companyId || null,
        })
      );

      if (res.user?.doctor_id !== undefined && res.user?.doctor_id !== null) {
        localStorage.setItem('doctor_id', String(res.user.doctor_id));
      } else {
        localStorage.removeItem('doctor_id');
      }

      if (res.user?.access_pages) {
        localStorage.setItem('access_pages', JSON.stringify(res.user.access_pages));
      }

      localStorage.setItem(
        'has_access_to_phone_numbers',
        String(!!res.user?.has_access_to_phone_numbers)
      );
    }

    this.loginService.setToken(res.token);
  }

  private isCompanyStep(value: any): value is CompanyStep {
    return value === 'company' || value === 'contact' || value === 'logo';
  }

  private normalizeId(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private canUseLocalStorage(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  private getStorageItem(key: string): string {
    if (!this.canUseLocalStorage()) {
      return '';
    }

    return localStorage.getItem(key) || '';
  }

  private setStorageItem(key: string, value: string): void {
    if (!this.canUseLocalStorage()) {
      return;
    }

    if (value) {
      localStorage.setItem(key, value);
    }
  }

  private removeStorageItem(key: string): void {
    if (!this.canUseLocalStorage()) {
      return;
    }

    localStorage.removeItem(key);
  }

  private getApiErrorMessage(error: any): string {
    if (error?.error?.errors) {
      const firstKey = Object.keys(error.error.errors)[0];
      return error.error.errors[firstKey][0];
    }

    return error?.error?.message || this.t('atw.createCompany.messages.serverError');
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}
